import React, { useContext, useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Spinner, Tabs, Tab } from "react-bootstrap";
import Filter from "./components/filter/filter";
import ReportContainer from "./components/report-container/report-container";
import AuditConfirmationContainer from "./components/audit-confirmation-container/audit-confirmation-container";
import CertificationOfHoldingsContainer from "./components/certification-of-holdings-container/certification-of-holdings-container";
import { useHistory } from "react-router-dom";
import ReportsService from "../../../../services/reports-service";
import "./checker-reports-module.scss";
import { IdentityContext } from "../../../../context/identity-context";

const CheckerReportsModule = () => {
  // Memoize the ReportsService instance so it doesn't change on each render.
  const reportsService = useMemo(() => new ReportsService(), []);

  // Data from API for each tab
  const [items, setItems] = useState([]);
  const [auditConfirmations, setAuditConfirmations] = useState([]);
  const [certificationHoldings, setCertificationHoldings] = useState([]);

  // Separate filtered states for each tab
  const [filteredItems, setFilteredItems] = useState([]);
  const [filteredAuditConfirmations, setFilteredAuditConfirmations] = useState([]);
  const [filteredCertificationHoldings, setFilteredCertificationHoldings] = useState([]);

  const [activeTab, setActiveTab] = useState("outstanding");
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Use a simple filter state (status and issuers) for the Filter component
  const [filter, setFilter] = useState({
    status: [],
    issuers: [],
  });

  const history = useHistory();

  // Load all reports on mount
  useEffect(() => {
    const getReports = async () => {
      try {
        const result = await reportsService.getReports("rr_checker");
        // Assuming:
        // - result.adhocReports for Outstanding Balances,
        // - result.consolidatedReports for Audit Confirmations,
        // - result.certificationReports for Certification of Holdings.
        setItems(result.adhocReports || []);
        setFilteredItems(result.adhocReports || []);

        setAuditConfirmations(result.consolidatedReports || []);
        setFilteredAuditConfirmations(result.consolidatedReports || []);

        setCertificationHoldings(result.certificationReports || []);
        setFilteredCertificationHoldings(result.certificationReports || []);

        console.log(result);
      } catch (error) {
        console.error("Fetching error:", error);
        history.push("/internal-server-error");
      } finally {
        setLoading(false);
      }
    };
    getReports();
  }, [reportsService, history]);

  // Handle filter changes based on the active tab
  const handleFilterChange = (filters) => {
    const { status, issuers } = filters;
    setFilter(filters); // update the filter state

    if (activeTab === "outstanding") {
      const filtered = items.filter((item) => {
        const matchesStatus = status.length ? status.includes(item.status) : true;
        const matchesIssuer = issuers.length ? issuers.includes(item.issuer) : true;
        return matchesStatus && matchesIssuer;
      });
      setFilteredItems(filtered);
    } else if (activeTab === "audit") {
      const filtered = auditConfirmations.filter((confirmation) => {
        const matchesStatus = status.length ? status.includes(confirmation.status) : true;
        const matchesIssuer = issuers.length ? issuers.includes(confirmation.issuer) : true;
        return matchesStatus && matchesIssuer;
      });
      setFilteredAuditConfirmations(filtered);
    } else if (activeTab === "certification") {
      const filtered = certificationHoldings.filter((cert) => {
        const matchesStatus = status.length ? status.includes(cert.status) : true;
        // For Certification, filter by bpId instead of issuer
        const matchesBpId = issuers.length ? issuers.includes(cert.bpId) : true;
        return matchesStatus && matchesBpId;
      });
      setFilteredCertificationHoldings(filtered);
    }
  };

  // Determine which dataset to use for filter options based on the active tab
  const filterItems =
    activeTab === "outstanding"
      ? items
      : activeTab === "audit"
      ? auditConfirmations
      : certificationHoldings;

  // Checkbox selection (for outstanding items)
  const handleCheckboxChange = (id) => {
    setSelectedReports((prev) => {
      const newSelectedReports = new Set(prev);
      if (newSelectedReports.has(id)) {
        newSelectedReports.delete(id);
      } else {
        newSelectedReports.add(id);
      }
      return newSelectedReports;
    });
  };

  return (
    <Container className="checker-reports-module-container">
      {loading ? (
        <div className="loading-container">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Tabs
            activeKey={activeTab}
            onSelect={(tab) => {
              setActiveTab(tab);
              // Optionally reset the filter when switching tabs
              setFilter({ status: [], issuers: [] });
            }}
            className="mb-3"
          >
            <Tab eventKey="outstanding" title="Outstanding Balances" />
            <Tab eventKey="audit" title="Audit Confirmation" />
            <Tab eventKey="certification" title="Certification of Holdings" />
          </Tabs>
          <Row>
            <Col xs={12} md={3} className="panel">
              <Filter
                items={filterItems}
                filter={filter}
                onFilterChange={handleFilterChange}
              />
            </Col>
            <Col xs={12} md={9}>
              {activeTab === "outstanding" && (
                <ReportContainer
                  filteredReports={filteredItems}
                  handleCheckboxChange={handleCheckboxChange}
                  selectedReports={selectedReports}
                />
              )}
              {activeTab === "audit" && (
                <AuditConfirmationContainer items={filteredAuditConfirmations} />
              )}
              {activeTab === "certification" && (
                <CertificationOfHoldingsContainer items={filteredCertificationHoldings} />
              )}
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default CheckerReportsModule;
